// Utility: fetch an image as a base64 data URL via a CORS-friendly proxy.
// Used by the share-poster PNG exporter so html-to-image can rasterise
// remote sprite icons without tainting the canvas.

const cache = new Map();

const PROXIES = [
  // weserv image proxy — strips referer, adds CORS headers
  (url) => `https://images.weserv.nl/?url=${encodeURIComponent(url.replace(/^https?:\/\//, ''))}`,
  // raw original (works if the host already sends CORS headers)
  (url) => url,
];

async function tryFetchDataUrl(url) {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function fetchAsDataUrl(originalUrl) {
  if (cache.has(originalUrl)) return cache.get(originalUrl);
  let lastErr = null;
  for (const make of PROXIES) {
    try {
      const u = make(originalUrl);
      const dataUrl = await tryFetchDataUrl(u);
      cache.set(originalUrl, dataUrl);
      return dataUrl;
    } catch (e) {
      lastErr = e;
    }
  }
  // give up — return original; html-to-image may render blank but at least we tried
  console.warn('Image proxy fallback failed:', originalUrl, lastErr);
  return originalUrl;
}

export async function prefetchAll(urls, onProgress) {
  let done = 0;
  const out = {};
  // Mobile Safari throttles and drops requests when we fire 100+ parallel
  // fetches. A small concurrency pool keeps every request alive and finishes
  // faster overall on cellular / low-power devices.
  const CONCURRENCY = 6;
  const queue = [...urls];
  async function worker() {
    while (queue.length) {
      const u = queue.shift();
      if (u === undefined) return;
      try {
        out[u] = await fetchAsDataUrl(u);
      } catch (e) {
        out[u] = u; // fallback: original URL
      }
      done += 1;
      onProgress?.(done, urls.length);
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
  return out;
}
